import React, { useState, useRef } from 'react';
import { FileTextIcon, DownloadIcon, TrashIcon, UploadIcon, XIcon, EyeIcon } from 'lucide-react';
import { Document } from '../types/condition';
type DocumentListProps = {
  conditionId: string;
  documents: Document[];
  onUpload: (conditionId: string, file: File) => void;
  onDelete: (conditionId: string, documentId: string) => void;
  onPreview: (document: Document) => void;
};
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}
function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
export function DocumentList({
  conditionId,
  documents,
  onUpload,
  onDelete,
  onPreview
}: DocumentListProps) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      onUpload(conditionId, files[0]);
    }
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      onUpload(conditionId, files[0]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  return <div className="p-4 bg-gray-50 border-t border-gray-200" data-id="element-183">
      <div className="flex items-center justify-between mb-3" data-id="element-184">
        <h4 className="text-sm font-semibold text-gray-900" data-id="element-185">
          Documents ({documents.length})
        </h4>
      </div>

      {/* Document List */}
      {documents.length > 0 && <div className="space-y-2 mb-4" data-id="element-186">
          {documents.map(doc => <div key={doc.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors" data-id="element-187">
              <div className="flex items-center gap-3 flex-1 min-w-0" data-id="element-188">
                <div className="p-2 bg-blue-50 rounded" data-id="element-189">
                  <FileTextIcon className="w-4 h-4 text-blue-600" data-id="element-190" />
                </div>
                <div className="flex-1 min-w-0" data-id="element-191">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-192">
                    {doc.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-193">
                    {formatFileSize(doc.size)} • Uploaded by {doc.uploadedBy} •{' '}
                    {formatDate(doc.uploadedAt)}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-1 ml-2" data-id="element-194">
                <button onClick={() => onPreview(doc)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors" title="Preview" aria-label="Preview document" data-id="element-195">
                  <EyeIcon className="w-4 h-4" data-id="element-196" />
                </button>
                <button onClick={() => {}} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors" title="Download" aria-label="Download document" data-id="element-197">
                  <DownloadIcon className="w-4 h-4" data-id="element-198" />
                </button>
                <button onClick={() => onDelete(conditionId, doc.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors" title="Delete" aria-label="Delete document" data-id="element-199">
                  <TrashIcon className="w-4 h-4" data-id="element-200" />
                </button>
              </div>
            </div>)}
        </div>}

      {/* Upload Area */}
      <div onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={`
          border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
          ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 bg-white'}
        `} onClick={() => fileInputRef.current?.click()} data-id="element-201">
        <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" data-id="element-202" />
        <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" data-id="element-203" />
        <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-204">
          Drop files here or click to upload
        </p>
        <p className="text-xs text-gray-500" data-id="element-205">
          PDF, DOC, DOCX, JPG, PNG up to 10MB
        </p>
      </div>
    </div>;
}